// Navigation (Menu Item) Variants Configuration
//
// MANDATORY when adding a variant here: give it a human-readable `name`. It is
// spread into SECTION_VARIANTS by ./index.ts and read through
// `getVariantDetails()` by the page builder and by **Widget Management**
// (`/admin/widget-management`), where that string is the only label identifying
// the widget before someone deletes it from live pages. Miss it and the screen
// falls back to the raw id.
//
// Full checklist for a new section or variant: top of ../sectionTypes.ts.

import { setMenuItem, setMenuItem1047, setMenuItem1060, setMenuItem2, setMenuItem2000, setMenuItem3, setMenuItem4, setMenuItem5 } from "@/store/slices/page";
import { Form_2 } from "@/components/organisms/admin/forms";
import NavBar from "@/widgets/navbar/NavBar1";
import type { PageState } from "@/store/slices/page";
import { MENU_ITEM_CONSTANTS, NAVBAR_1_CONSTANTS } from "../fields/menu/menuItem_1";
import { MENU_ITEM_2_CONSTANTS, NAVBAR_2_CONSTANTS } from "../fields/menu/menuItem_2";
import { MENU_ITEM_3_CONSTANTS, NAVBAR_3_CONSTANTS } from "../fields/menu/menuItem_3";
import { MENU_ITEM_4_CONSTANTS, NAVBAR_4_CONSTANTS } from "../fields/menu/menuItem_4";
import NavBar3 from "@/widgets/navbar/NavBar3";
import NavBar4 from "@/widgets/navbar/NavBar4";
import { MENU_ITEM_5_CONSTANTS, NAVBAR_5_CONSTANTS } from "../fields/menu/menuItem_5";
import NavBar5 from "@/widgets/navbar/NavBar5";
import {
  createMenuItem,
  updateMenuItem,
  getMenuItemsByNavbarId,
  deleteMenuItem,
} from "@/api/admin/menuItem";
import { createNavBar, updateNavBar, getNavBarById, cloneNavBarWithMenuItems } from "@/api/admin/navBar";
import { SECTION_TYPES, MENU_ITEM_VARIANTS } from "../sectionTypes";
import NavBar2 from "@/widgets/navbar/NavBar2";
import NavBar2000 from "@/widgets/navbar/NavBar2000";
import { MENU_ITEM_2000_CONSTANTS, NAVBAR_2000_CONSTANTS } from "../fields/menu/menuItem_2000";
import NavBar1047 from "@/widgets/navbar/NavBar1047";
import NavBar1060 from "@/widgets/navbar/NavBar1060";
import { MENU_ITEM_1047_CONSTANTS, NAVBAR_1047_CONSTANTS } from "../fields/menu/menuItem_1047";
import { MENU_ITEM_1060_CONSTANTS, NAVBAR_1060_CONSTANTS } from "../fields/menu/menuItem_1060";

// Helper to activate menu items recursively
const activateMenuItems = (items: any[]): any[] =>
  items?.map((item) => ({
    ...item,
    children: item.children ? activateMenuItems(item.children) : [],
  })) || [];

export const NAVIGATION_SECTION_VARIANTS = {
  [SECTION_TYPES.MENU_ITEM]: [
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_1,
      name: "Menu Item 1",
      description: "Menu Item 1",
      componentFields: MENU_ITEM_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_1_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem,
      formComponent: Form_2,
      previewComponent: NavBar,
      selector: (state: PageState) => state.menu_item,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              background_color: data.background_color || "#ffffff",
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_2,
      name: "Menu Item 2",
      description: "Menu Item 2",
      componentFields: MENU_ITEM_2_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_2_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem2,
      formComponent: Form_2,
      previewComponent: NavBar2,
      selector: (state: PageState) => state.menu_item_2,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_2_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              background_color: data.background_color || "#ffffff",
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              program_name: data.program_name || "",
              program_name_text_color: data.program_name_text_color || "#000000",
              horizontal_stripe_color: data.horizontal_stripe_color || "#BE2330",
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_5,
      name: "Menu Item 5",
      description: "Menu Item 5",
      componentFields: MENU_ITEM_5_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_5_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem5,
      formComponent: Form_2,
      previewComponent: NavBar5,
      selector: (state: PageState) => state.menu_item_5,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_5_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              background_color: data.background_color || "#ffffff",
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              mobile_logo_icon: data.mobile_logo_icon || "",
              mobile_logo_icon_alt: data.mobile_logo_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              nav_link_color: data.nav_link_color || "",
              sticky_to_top: data.sticky_to_top === true,
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_4,
      name: "Menu Item 4",
      description: "Menu Item 4",
      componentFields: MENU_ITEM_4_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_4_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem4,
      formComponent: Form_2,
      previewComponent: NavBar4,
      selector: (state: PageState) => state.menu_item_4,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_4_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              background_color: data.background_color || "#ffffff",
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              mobile_logo_icon: data.mobile_logo_icon || "",
              mobile_logo_icon_alt: data.mobile_logo_icon_alt || "",
              nav_link_color: data.nav_link_color || "",
              nav_link_hover_color: data.nav_link_hover_color || "",
              sticky_to_top: data.sticky_to_top === true,
              mobile_hamburger_background:
                data.mobile_hamburger_background || "",
              mobile_menu_background: data.mobile_menu_background || "",
              mobile_combined_cta_label: data.mobile_combined_cta_label || "",
              mobile_combined_cta_background:
                data.mobile_combined_cta_background || "",
              mobile_combined_cta_border_color:
                data.mobile_combined_cta_border_color || "",
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_3,
      name: "Nav Header 3",
      description: "Combined sticky announcement bar + navigation",
      componentFields: MENU_ITEM_3_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_3_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem3,
      formComponent: Form_2,
      previewComponent: NavBar3,
      selector: (state: PageState) => state.menu_item_3,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_3_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              // Announcement bar
              announcement_bar_bg_color: data.announcement_bar_bg_color || "#FFFFFF",
              announcement_text: data.announcement_text ?? "",
              announcement_text_color: data.announcement_text_color || "#9F1C34",
              announcement_bg_color: data.announcement_bg_color || "#F2E0E3",
              announcement_border_color: data.announcement_border_color || "#9F1C34",
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              logo_size_desktop: data.logo_size_desktop || "small",
              logo_size_mobile: data.logo_size_mobile || "small",
              // `??` not `||`: false must survive, and null/undefined means "on".
              logo_redirect_enabled: data.logo_redirect_enabled ?? true,
              // Nav row
              background_color: data.background_color || "#9F1C34",
              nav_text_color: data.nav_text_color || "#FFFFFF",
              active_menu_color: data.active_menu_color || "#F4BEBE",
              border_color: data.border_color || "rgba(0,0,0,0.2)",
              // Mobile-specific color overrides
              mobile_announcement_bg_color: data.mobile_announcement_bg_color || "#9F1C34",
              mobile_announcement_text_color: data.mobile_announcement_text_color || "#FFFFFF",
              mobile_announcement_border_color: data.mobile_announcement_border_color || "#9F1C34",
              mobile_menu_text_color: data.mobile_menu_text_color || "#000000",
              mobile_active_menu_color: data.mobile_active_menu_color || "#9F1C34",
              // Dropdown
              dropdown_bg_color: data.dropdown_bg_color || "#F2E0E3",
              dropdown_text_color: data.dropdown_text_color || "#000000",
              show_dropdown_icons: data.show_dropdown_icons ?? true,
              // CTA-1
              cta1_enabled: data.cta1_enabled ?? true,
              cta1_icon: data.cta1_icon || "",
              cta1_icon_alt: data.cta1_icon_alt || "Call",
              cta1_label: data.cta1_label ?? "+91 1234567890",
              cta1_href: data.cta1_href || "",
              cta1_bg_color: data.cta1_bg_color || "transparent",
              cta1_text_color: data.cta1_text_color || "#000000",
              cta1_border_color: data.cta1_border_color || "#000000",
              // CTA-2
              cta2_enabled: data.cta2_enabled ?? true,
              cta2_icon: data.cta2_icon || "",
              cta2_icon_alt: data.cta2_icon_alt || "WhatsApp",
              cta2_label: data.cta2_label ?? "WhatsApp Chat",
              cta2_href: data.cta2_href || "",
              cta2_bg_color: data.cta2_bg_color || "#4CAF51",
              cta2_text_color: data.cta2_text_color || "#FFFFFF",
              cta2_border_color: data.cta2_border_color || "transparent",
              // CTA-3 (optional — a blank label hides the button, so no fallback here)
              cta3_label: data.cta3_label ?? "",
              cta3_icon: data.cta3_icon || "",
              cta3_icon_alt: data.cta3_icon_alt || "Apply Now",
              cta3_bg_color: data.cta3_bg_color || "#9F1C34",
              cta3_text_color: data.cta3_text_color || "#FFFFFF",
              // Mobile chrome
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_2000,
      name: "Menu Item 2000",
      description: "Black nav bar — logo + links + phone + WhatsApp + mobile hamburger",
      componentFields: MENU_ITEM_2000_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_2000_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem2000,
      formComponent: Form_2,
      previewComponent: NavBar2000,
      selector: (state: PageState) => state.menu_item_2000,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_2000_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              // Bar
              background_color: data.background_color || "#000000",
              nav_text_color: data.nav_text_color || "#FFFFFF",
              active_menu_color: data.active_menu_color || "#FFFFFF",
              border_color: data.border_color || "rgba(255,255,255,0.2)",
              mobile_menu_text_color: data.mobile_menu_text_color || "#FFFFFF",
              mobile_active_menu_color: data.mobile_active_menu_color || "#FFFFFF",
              // Dropdown
              dropdown_bg_color: data.dropdown_bg_color || "#111111",
              dropdown_text_color: data.dropdown_text_color || "#FFFFFF",
              show_dropdown_icons: data.show_dropdown_icons ?? true,
              // CTA-1 (phone)
              cta1_enabled: data.cta1_enabled ?? true,
              cta1_icon: data.cta1_icon || "",
              cta1_icon_alt: data.cta1_icon_alt || "Call",
              cta1_label: data.cta1_label ?? "+91 7973615772",
              cta1_href: data.cta1_href || "",
              cta1_bg_color: data.cta1_bg_color || "rgba(0,0,0,0.1)",
              cta1_text_color: data.cta1_text_color || "#FFFFFF",
              cta1_border_color: data.cta1_border_color || "#FFFFFF",
              // CTA-2 (WhatsApp)
              cta2_enabled: data.cta2_enabled ?? true,
              cta2_icon: data.cta2_icon || "",
              cta2_icon_alt: data.cta2_icon_alt || "WhatsApp",
              cta2_href: data.cta2_href || "",
              cta2_bg_color: data.cta2_bg_color || "#4CAF51",
              cta2_text_color: data.cta2_text_color || "#FFFFFF",
              // CTA-3 (Apply Now — desktop bar only)
              cta3_enabled: data.cta3_enabled ?? false,
              cta3_label: data.cta3_label || "APPLY NOW",
              cta3_href: data.cta3_href || "",
              cta3_bg_color: data.cta3_bg_color || "#D61229",
              cta3_text_color: data.cta3_text_color || "#FFFFFF",
              // Mobile chrome
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_1060,
      name: "Menu Item 1060",
      description:
        "KUK site nav — white bar, crest + wordmark, links with 3-level dropdowns and a grey active pill, brand-filled Sign Up CTA",
      componentFields: MENU_ITEM_1060_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_1060_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem1060,
      formComponent: Form_2,
      previewComponent: NavBar1060,
      selector: (state: PageState) => state.menu_item_1060,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_1060_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              // Both fall back to the bundled files in public/icons, so a nav
              // that has never been saved still previews with real logos.
              logo_icon: data.logo_icon || "/icons/kuk-logo.png",
              logo_icon_alt: data.logo_icon_alt || "KUK University",
              mobile_logo_icon:
                data.mobile_logo_icon || "/icons/kuk-university-print.png",
              mobile_logo_icon_alt: data.mobile_logo_icon_alt || "KUK University",
              // Bar
              background_color: data.background_color || "#FFFFFF",
              nav_text_color: data.nav_text_color || "#1B1B1B",
              active_menu_color: data.active_menu_color || "#EAECF0",
              border_color: data.border_color || "#EAECF0",
              mobile_menu_text_color: data.mobile_menu_text_color || "#1B1B1B",
              show_bar_shadow: data.show_bar_shadow !== false,
              // Dropdown
              dropdown_bg_color: data.dropdown_bg_color || "#FFFFFF",
              dropdown_text_color: data.dropdown_text_color || "#1B1B1B",
              show_dropdown_icons: data.show_dropdown_icons ?? false,
              // CTA (Sign Up)
              cta3_enabled: data.cta3_enabled ?? true,
              cta3_label: data.cta3_label || "Sign Up",
              cta3_href: data.cta3_href || "",
              cta3_bg_color: data.cta3_bg_color || "#9F1C33",
              cta3_text_color: data.cta3_text_color || "#FFFFFF",
              // Mobile chrome
              hamburger_icon: data.hamburger_icon || "",
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              sticky_to_top: data.sticky_to_top !== false,
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
    {
      id: MENU_ITEM_VARIANTS.MENU_ITEM_1047,
      name: "Menu Item 1047",
      description:
        "BDU nav — white bar, logo + links with 3-level dropdowns + gradient Apply CTA",
      componentFields: MENU_ITEM_1047_CONSTANTS.MENU_FIELDS_CONFIG,
      parentComponentFields: NAVBAR_1047_CONSTANTS.NAVBAR_FIELDS_CONFIG,
      showTable: false,
      setAction: setMenuItem1047,
      formComponent: Form_2,
      previewComponent: NavBar1047,
      selector: (state: PageState) => state.menu_item_1047,
      createMethod: createMenuItem,
      updateMethod: updateMenuItem,
      fetchMethod: getMenuItemsByNavbarId,
      deleteMethod: deleteMenuItem,
      treeConfig: MENU_ITEM_1047_CONSTANTS.TREE_CONFIG,
      parentComponentCreateMethod: createNavBar,
      parentComponentUpdateMethod: updateNavBar,
      parentComponentGetMethod: getNavBarById,
      cloneMethod: cloneNavBarWithMenuItems,
      cloneEntityName: "Navbar",
      cloneNestedEntities: "menu items",
      hasParentChildStructure: true,
      childItemsFieldName: "menuItems",
      preparePreviewData: ({ section }: any) => {
        // Parent/child widgets use section.data directly
        const data = section.data || {};
        let menuItems: any[] = [];

        if (data.menuItems && Array.isArray(data.menuItems)) {
          menuItems = data.menuItems;
        } else if (Array.isArray(data)) {
          menuItems = data;
        }

        return {
          data: {
            data: {
              menuItems: activateMenuItems(menuItems),
              logo_icon: data.logo_icon || "",
              logo_icon_alt: data.logo_icon_alt || "",
              mobile_logo_icon: data.mobile_logo_icon || "",
              mobile_logo_icon_alt: data.mobile_logo_icon_alt || "",
              // Bar
              background_color: data.background_color || "#FFFFFF",
              nav_text_color: data.nav_text_color || "#1B1B1B",
              active_menu_color: data.active_menu_color || "#FF0013",
              active_menu_color_2: data.active_menu_color_2 ?? "#990013",
              border_color: data.border_color || "rgba(0,0,0,0.1)",
              mobile_menu_text_color: data.mobile_menu_text_color || "#1B1B1B",
              // Dropdown
              dropdown_bg_color: data.dropdown_bg_color || "#FFFFFF",
              dropdown_text_color: data.dropdown_text_color || "#1B1B1B",
              show_dropdown_icons: data.show_dropdown_icons ?? false,
              // CTA (Apply)
              cta3_enabled: data.cta3_enabled ?? true,
              cta3_label: data.cta3_label || "Apply",
              cta3_href: data.cta3_href || "",
              cta3_bg_color: data.cta3_bg_color || "#FF0013",
              cta3_bg_color_2: data.cta3_bg_color_2 ?? "#990013",
              cta3_text_color: data.cta3_text_color || "#FFFFFF",
              // Mobile chrome
              hamburger_icon: data.hamburger_icon || "",
              // `??` so an explicit off survives; missing = show (legacy rows).
              show_hamburger: data.show_hamburger ?? true,
              hamburger_icon_alt: data.hamburger_icon_alt || "",
              mobile_cancel_icon: data.mobile_cancel_icon || "",
              mobile_cancel_icon_alt: data.mobile_cancel_icon_alt || "",
              sticky_to_top: data.sticky_to_top !== false,
              status: true,
            },
            isPreview: true,
          },
        };
      },
    },
  ],
};
